import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { productPath } from '../utils/productUrl';
import ProductsImg from '../assets/image/Products.jpg';

// ─── Styles (from static version, extended for dynamic features)
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&display=swap');

  .prods-root * { box-sizing: border-box; }
  .prods-root {
    font-family: 'Playfair Display', Georgia, serif;
    background: #f8fbfc;
    min-height: 100vh;
    color: #162033;
  }

  /* ── Hero ── */
  .prods-hero {
    background: linear-gradient(135deg, #2eb4b4 0%, #174f9a 100%);
    position: relative;
    overflow: hidden;
    padding: 0;
  }
  .prods-hero-img {
    width: 100%;
    height: auto;
    display: block;
  }
  .prods-hero-content {
    padding: 80px 24px 64px;
  }

  .prods-hero-overlay {
    position: absolute; inset: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%);
    pointer-events: none;
  }
  .prods-hero-inner {
    max-width: 900px;
    margin: 0 auto;
    text-align: center;
    position: relative;
    z-index: 2;
  }
  .prods-hero-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 50px;
    padding: 8px 20px;
    margin-bottom: 28px;
    color: rgba(255,255,255,0.95);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }
  .prods-hero-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: rgba(255,255,255,0.9);
  }
  .prods-hero h1 {
    font-size: clamp(2.5rem, 7vw, 4.5rem);
    font-weight: 700;
    color: white;
    line-height: 1.1;
    margin: 0 0 24px;
    letter-spacing: -0.02em;
  }
  .prods-hero-sub {
    color: rgba(255,255,255,0.9);
    font-size: 17px;
    font-weight: 400;
    margin-bottom: 48px;
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  /* ── Search ── */
  .prods-search-wrap {
    position: relative;
    max-width: 500px;
    margin: 0 auto;
  }
  .prods-search-input {
    width: 100%;
    background: rgba(255,255,255,0.95);
    border: 2px solid transparent;
    color: #162033;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 15px;
    font-weight: 400;
    padding: 16px 52px 16px 52px;
    border-radius: 50px;
    outline: none;
    transition: all 0.3s ease;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }
  .prods-search-input::placeholder { color: rgba(22,32,51,0.5); }
  .prods-search-input:focus {
    background: white;
    border-color: rgba(255,255,255,0.8);
    box-shadow: 0 12px 32px rgba(0,0,0,0.16);
  }
  .prods-search-icon {
    position: absolute;
    left: 20px; top: 50%;
    transform: translateY(-50%);
    color: #2eb4b4;
    pointer-events: none;
  }
  .prods-search-clear {
    position: absolute;
    right: 16px; top: 50%;
    transform: translateY(-50%);
    width: 28px; height: 28px;
    border-radius: 50%;
    border: none;
    background: #f0f0f0;
    color: #666;
    cursor: pointer;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  .prods-search-clear:hover { background: #e0e0e0; color: #333; }

  /* ── Filter bar ── */
  .prods-filterbar {
    background: white;
    border-bottom: 1px solid #e8f0fa;
    position: sticky;
    top: 0;
    z-index: 30;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }

  /* ── Sort select ── */
  .prods-sort-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .prods-sort-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #174f9a;
    white-space: nowrap;
  }
  .prods-sort-select {
    appearance: none;
    background: #f8fbfc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23174f9a' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
    border: 2px solid #e8f0fa;
    border-radius: 50px;
    padding: 8px 32px 8px 14px;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 13px;
    font-weight: 500;
    color: #162033;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;
  }
  .prods-sort-select:focus { border-color: #2eb4b4; }

  /* ── Rx toggle ── */
  .prods-rx-toggle {
      display: flex;
      align-items: center;
      gap: 12px;
  }
  .prods-rx-label {
    font-size: 13px;
    font-weight: 500;
    color: #162033;
    white-space: nowrap;
  }
  .prods-toggle-track {
    width: 44px; height: 24px;
    border-radius: 12px;
    background: #e8f0fa;
    position: relative;
    cursor: pointer;
    transition: background 0.3s ease;
    flex-shrink: 0;
    border: none;
    padding: 0;
  }
  .prods-toggle-track.on {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
  }
  .prods-toggle-thumb {
    position: absolute;
    top: 3px; left: 3px;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: white;
    transition: transform 0.3s ease;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }
  .prods-toggle-track.on .prods-toggle-thumb {
    transform: translateX(20px);
  }

  /* ── Results count ── */
.prods-results-count {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}
  /* ── Chemical category inline filter strip ── */
  .prods-chem-strip {
    background: linear-gradient(135deg, rgba(46,180,180,0.06), rgba(23,79,154,0.06));
    border-bottom: 1px solid rgba(46,180,180,0.15);
    overflow-x: hidden;
  }
  .prods-chem-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border-radius: 50px;
    border: 1.5px solid #e8f0fa;
    background: white;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 12.5px;
    font-weight: 500;
    color: #162033;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.25s ease;
    flex-shrink: 0;
  }
  .prods-chem-pill:hover {
    border-color: #2eb4b4;
    color: #2eb4b4;
    background: rgba(46,180,180,0.05);
  }
  .prods-chem-pill.active {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    border-color: transparent;
    color: white;
    box-shadow: 0 4px 12px rgba(46,180,180,0.3);
  }
  .prods-chem-pill-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.6;
    flex-shrink: 0;
  }
  .prods-chem-clear {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 50px;
    border: 1.5px dashed #e8f0fa;
    background: transparent;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s;
  }
  .prods-chem-clear:hover {
    border-color: #dc2626;
    color: #dc2626;
    background: rgba(220,38,38,0.04);
  }

  /* ── Grid ── */
  .prods-grid-section {
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 24px 100px;
  }
  .prods-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 24px;
  }
  @media (max-width: 640px) {
    .prods-grid {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: 16px;
    }
  }

  /* ── Card ── */
  .prods-card {
    background: white;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    animation: cardIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .prods-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.12);
  }
  .prods-card:hover .prods-card-img { transform: scale(1.06); }

  .prods-card-img-wrap {
    position: relative;
    overflow: hidden;
    aspect-ratio: 1 / 1;
    background: #ffffff;
  }
  .prods-card-img {
    width: 100%; height: 100%;
    object-fit: contain;
    object-position: center;
    padding: 38px 18px 4px;
    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    display: block;
  }
  .prods-card-img-fallback {
    width: 100%; height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e6f7f7, #eff6ff);
  }
  .prods-card-img-fallback-text {
    font-size: 56px;
    font-weight: 700;
    color: #2eb4b4;
    opacity: 0.2;
  }

  .prods-card-badge {
    position: absolute;
    top: 12px; right: 12px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 5px 12px;
    border-radius: 50px;
    backdrop-filter: blur(12px);
  }
  .prods-card-badge.rx  { background: rgba(220,38,38,0.95);  color: white; }
  .prods-card-badge.otc { background: rgba(5,150,105,0.95);  color: white; }
  .prods-card-badge.status {
    background: rgba(255,255,255,0.92);
    color: #059669;
  }

  .prods-card-body {
    padding: 18px;
    text-align: center;
  }
  .prods-card-cat {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #2eb4b4;
    margin-bottom: 6px;
  }
  .prods-card-name {
    font-size: 1.05rem;
    font-weight: 600;
    color: #162033;
    line-height: 1.3;
  }
  .prods-card-div {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
  }

  /* ── Skeleton cards ── */
  .prods-skeleton {
    border-radius: 12px;
    overflow: hidden;
    background: white;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }
  .prods-skeleton-img {
    aspect-ratio: 1/1;
    background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
  }
  .prods-skeleton-body {
    padding: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .prods-skeleton-line {
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* ── Empty state ── */
  .prods-empty {
    text-align: center;
    padding: 100px 24px;
  }
  .prods-empty-title {
    font-size: 2rem;
    color: #162033;
    font-weight: 600;
    margin-bottom: 12px;
  }
  .prods-empty-sub {
    font-size: 15px;
    color: #666;
    margin-bottom: 32px;
  }
  .prods-empty-btn {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white;
    border: none;
    padding: 14px 32px;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 50px;
    transition: all 0.3s ease;
  }
  .prods-empty-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(46,180,180,0.3);
  }

  /* ── Pagination ── */
  .prods-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 56px;
  }
  .prods-page-btn {
    border: 2px solid #e8f0fa;
    background: white;
    padding: 12px 24px;
    border-radius: 50px;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 13px;
    font-weight: 600;
    color: #162033;
    cursor: pointer;
    transition: all 0.25s;
  }
  .prods-page-btn:hover:not(:disabled) {
    border-color: #2eb4b4;
    color: #2eb4b4;
  }
  .prods-page-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  .prods-page-current {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white;
    padding: 12px 24px;
    border-radius: 50px;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 13px;
    font-weight: 700;
  }

  /* ── Error banner ── */
  .prods-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 24px;
    font-size: 14px;
    color: #dc2626;
    text-align: center;
  }

  /* ── Footer CTA ── */
  .prods-footer-cta {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    padding: 80px 24px;
    text-align: center;
  }
  .prods-footer-cta-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.8);
    margin-bottom: 16px;
  }
  .prods-footer-cta h2 {
    font-size: clamp(1.75rem, 5vw, 3rem);
    font-weight: 700;
    color: white;
    margin-bottom: 36px;
    letter-spacing: -0.01em;
  }
  .prods-footer-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: white;
    color: #174f9a;
    border: none;
    padding: 16px 36px;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 50px;
    text-decoration: none;
    transition: all 0.3s ease;
  }
  .prods-footer-cta-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.2);
  }

  @media (max-width: 640px) {
    .prods-hero { padding: 80px 20px 64px; }
    .prods-grid-section { padding: 48px 16px 80px; }
    .prods-card-name { font-size: 0.95rem; }
  }

  /* ========== Unified filter bar system (Category + Chemical Category) ========== */
  .pf-bar {
    max-width: 1200px;
    margin: 12px auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .prods-filterbar .pf-bar { min-height: 60px; }
  .prods-chem-strip .pf-bar { min-height: 52px; }

  .pf-label {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #174f9a;
    white-space: nowrap;
    padding-right: 16px;
    margin-right: 16px;
    border-right: 1px solid #e8f0fa;
  }
  .pf-label svg { opacity: 0.6; flex-shrink: 0; }

  .pf-scroll {
    position: relative;
    flex: 1 1 auto;
    min-width: 0; /* critical — lets the track actually scroll instead of forcing siblings to shrink to 0 */
    overflow: hidden;
    padding: 0 36px;
  }
  .pf-track {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    padding: 14px 0;
    scroll-padding: 0 36px;
    -webkit-overflow-scrolling: touch;
  }
  .pf-track::-webkit-scrollbar { display: none; }

  .pf-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 6;
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 1.5px solid #e8f0fa;
    background: white;
    box-shadow: 0 4px 14px rgba(0,0,0,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #174f9a;
    opacity: 1;
    pointer-events: auto;
    transition: opacity .2s, background .2s, color .2s, transform .2s;
  }
  .pf-arrow.disabled {
    opacity: 1;
    cursor: pointer;
    pointer-events: auto;
  }
  .pf-arrow:hover {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: #fff; border-color: transparent;
    transform: translateY(-50%) scale(1.08);
  }
  .pf-arrow.left  { left: 4px; }
  .pf-arrow.right { right: 4px; }

  .pf-fade {
    position: absolute;
    top: 0; bottom: 0;
    width: 36px;
    z-index: 5;
    pointer-events: none;
    opacity: 0;
    transition: opacity .2s;
  }
  .pf-fade.visible { opacity: 1; }
  .pf-fade.left  { left: 0; background: linear-gradient(to right, white, rgba(255,255,255,0)); }
  .pf-fade.right { right: 0; background: linear-gradient(to left, white, rgba(255,255,255,0)); }
  .prods-chem-strip .pf-fade.left  { background: linear-gradient(to right, #eef7f7, rgba(238,247,247,0)); }
  .prods-chem-strip .pf-fade.right { background: linear-gradient(to left, #eef7f7, rgba(238,247,247,0)); }

  .pf-controls {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    padding-left: 20px;
    margin-left: 4px;
    border-left: 1px solid #e8f0fa;
  }

@media (max-width: 860px) {
    .pf-label {
      border-right: none;
      margin-right: 0;
      padding-right: 0;
      padding-bottom: 6px;
      flex-basis: 100%;
      font-size: 11px;
    }
    .pf-scroll { flex-basis: 100%; order: 2; padding: 0; }
    .pf-track { padding: 10px 4px; gap: 6px; }
    .pf-arrow { display: none; }

    .pf-controls {
      flex-basis: 100%;
      order: 3;
      flex-direction: column;
      align-items: stretch;
      border-left: none;
      padding-left: 0;
      margin-left: 0;
      padding-top: 10px;
      margin-top: 6px;
      border-top: 1px solid #e8f0fa;
      gap: 10px;
    }
    .prods-sort-wrap,
    .prods-rx-toggle {
      width: 100%;
      justify-content: space-between;
    }
    .prods-rx-toggle { white-space: nowrap; }
    .prods-results-count {
      width: 100%;
      text-align: left;
      padding-top: 2px;
    }
  }

  @media (max-width: 480px) {
    .pf-bar { padding: 0 14px; }
    .prods-filterbar .pf-bar { padding-top: 10px; padding-bottom: 10px; }
    .prods-chem-strip .pf-bar { padding-top: 10px; padding-bottom: 10px; }
    .prods-filter-btn { padding: 8px 16px; font-size: 13px; }
    .prods-chem-pill { padding: 6px 13px; font-size: 12px; }
    .pf-label { font-size: 10.5px; }
  }

  /* Category buttons live inside pf-track now */
  .prods-filter-btn {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: transparent;
    border: 2px solid transparent;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 14px;
    font-weight: 500;
    color: #162033;
    flex-shrink: 0;
  }
  .prods-filter-btn:hover { background: #f8fbfc; border-color: #e8f0fa; }
  .prods-filter-btn.active {
    background: linear-gradient(135deg, #2eb4b4, #174f9a);
    color: white;
    border-color: transparent;
  }
  .prods-filter-count {
    font-size: 11px;
    font-weight: 600;
    background: rgba(46,180,180,0.15);
    color: #2eb4b4;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .prods-filter-btn.active .prods-filter-count {
    background: rgba(255,255,255,0.25);
    color: white;
  }

  @media (max-width: 480px) {
    .prods-filter-btn { padding: 8px 16px; font-size: 13px; }
    .prods-chem-pill { padding: 6px 13px; font-size: 12px; }
  }
`;

// ─── SVG Icons
const SearchIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const ArrowIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const FlaskIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M9 3h6M9 3v7l-4 8a1 1 0 0 0 .9 1.5h12.2A1 1 0 0 0 19 18l-4-8V3"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

// ─── Toggle
function Toggle({ on, onToggle }) {
  return (
    <button
      className={`prods-toggle-track${on ? ' on' : ''}`}
      onClick={onToggle}
      aria-pressed={on}
      type="button"
    >
      <span className="prods-toggle-thumb" />
    </button>
  );
}

// ─── Skeleton Card
function SkeletonCard({ index }) {
  return (
    <div className="prods-skeleton" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="prods-skeleton-img" />
      <div className="prods-skeleton-body">
        <div className="prods-skeleton-line" style={{ width: '40%', height: 10 }} />
        <div className="prods-skeleton-line" style={{ width: '80%', height: 14 }} />
        <div className="prods-skeleton-line" style={{ width: '55%', height: 10 }} />
      </div>
    </div>
  );
}

// ─── Product Card
function ProductCard({ product: p, onClick, index }) {
  const [imgErr, setImgErr] = useState(false);
  const firstImg = p.images?.[0];
  const imgSrc = typeof firstImg === 'object' ? firstImg?.url : firstImg;

  return (
    <article
      className="prods-card"
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={onClick}
    >
      <div className="prods-card-img-wrap">
        {!imgErr && imgSrc ? (
          <img
            src={imgSrc}
            alt={p.name}
            onError={() => setImgErr(true)}
            className="prods-card-img"
          />
        ) : (
          <div className="prods-card-img-fallback">
            <span className="prods-card-img-fallback-text">
              {p.name.slice(0, 2).toUpperCase()}
            </span>
          </div>
        )}
        {p.status ? (
          <span className="prods-card-badge status">{p.status}</span>
        ) : (p.rx_required !== undefined || p.rxRequired !== undefined) ? (
          <span className={`prods-card-badge ${p.rx_required === true || p.rxRequired === true ? 'rx' : 'otc'}`}>
            {p.rx_required === true || p.rxRequired === true ? 'Rx Only' : 'OTC'}
          </span>
        ) : null}
      </div>

      <div className="prods-card-body">
        {(p.product_category_name || p.subcategory) && (
          <p className="prods-card-cat">{p.product_category_name || p.subcategory}</p>
        )}
        <h3 className="prods-card-name">{p.name}</h3>
        {(p.division_name || p.brand) && (
          <p className="prods-card-div">{p.division_name || p.brand}</p>
        )}
      </div>
    </article>
  );
}

function useHorizontalScroll() {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // Reacts whenever pills are added/removed/resized (e.g. async API data arriving)
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
  }, []);

  const scrollByAmount = (amount) => ref.current?.scrollBy({ left: amount, behavior: 'smooth' });

  return { ref, canLeft, canRight, update, scrollByAmount };
}

// ─── Main Component
export default function Products() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chemicalCategoryParam = searchParams.get('chemical_category') || '';
  const divisionParam         = searchParams.get('division') || '';

  // Filter/sort state
  const [search,           setSearch          ] = useState('');
  const [activeCategory,   setActiveCategory  ] = useState('All');
  const [chemicalCategory, setChemicalCategory] = useState(chemicalCategoryParam);
  const [sort,             setSort            ] = useState('order');
  const [rxOnly,           setRxOnly          ] = useState(false);
  const [page,             setPage            ] = useState(1);

  // Data state
  const [products,           setProducts          ] = useState([]);
  const [categories,         setCategories        ] = useState([]);
  const [chemicalCategories, setChemicalCategories] = useState([]);
  const [meta,               setMeta              ] = useState({ count: 0, total_pages: 1 });
  const [loading,            setLoading           ] = useState(true);
  const [error,              setError             ] = useState('');

  const filterScroll = useHorizontalScroll();
  const chemScroll   = useHorizontalScroll();

  useEffect(() => { filterScroll.update(); }, [categories, activeCategory]);
  useEffect(() => { chemScroll.update(); }, [chemicalCategories, chemicalCategory]);

  // Inject styles once
  const styleInjected = useRef(false);
  useEffect(() => {
    if (styleInjected.current) return;
    styleInjected.current = true;
    const el = document.createElement('style');
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);

  // Sync URL param → state on mount / URL change
  useEffect(() => {
    setChemicalCategory(chemicalCategoryParam);
    setPage(1);
  }, [chemicalCategoryParam]);

  // Load filter options (categories + chemical categories)
  useEffect(() => {
    Promise.all([
      api.categories({ page_size: 100 }),
      api.chemicalCategories({ page_size: 100 }),
    ]).then(([cat, chem]) => {
      setCategories(cat.results || []);
      setChemicalCategories(chem.results || []);
    }).catch(() => {});
  }, []);

  // Load products with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      const params = {
        page,
        page_size: 12,
        search,
        chemical_category: chemicalCategory,
        ...(divisionParam && { division: divisionParam }),
        ...(activeCategory !== 'All' && { category: activeCategory }),
      };
      api.products(params)
        .then(data => {
          setProducts(data.results || []);
          setMeta(data);
          setError('');
        })
        .catch(err => setError(err.message || 'Failed to load products.'))
        .finally(() => setLoading(false));
    }, 280);
    return () => clearTimeout(timer);
  }, [page, search, chemicalCategory, activeCategory, divisionParam]);

  // Client-side sort + rx filter
  const displayed = useMemo(() => {
    let list = [...products];
    if (rxOnly) list = list.filter(p => p.rx_required === true || p.rxRequired === true);
    if (sort === 'order')     list.sort((a, b) => (a.order_no || 0) - (b.order_no || 0) || a.name.localeCompare(b.name));
    if (sort === 'name')      list.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'name_desc') list.sort((a, b) => b.name.localeCompare(a.name));
    if (sort === 'newest')    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, sort, rxOnly]);

  const totalCount = meta.count || 0;

  const activeChemLabel = chemicalCategories.find(
    c => String(c.id) === String(chemicalCategory)
  )?.name;

  const resetAll = () => {
    setSearch('');
    setActiveCategory('All');
    setChemicalCategory('');
    setRxOnly(false);
    setSort('order');
    setPage(1);
  };

  const handleChemSelect = (id) => {
    setChemicalCategory(prev => (String(prev) === String(id) ? '' : String(id)));
    setPage(1);
  };

  return (
    <div className="prods-root">

      {/* ── HERO ── */}
      <img src={ProductsImg} alt="Products" className="prods-hero-img" />

      {/* ── CHEMICAL CATEGORY STRIP ── */}
      {chemicalCategories.length > 0 && (
        <div className="prods-chem-strip">
          <div className="pf-bar">
            <span className="pf-label">
              <FlaskIcon /> Chemical Category
            </span>

            <div className="pf-scroll">
              <button
                className={`pf-arrow left${chemScroll.canLeft ? '' : ' disabled'}`}
                onClick={() => chemScroll.scrollByAmount(-220)}
                aria-label="Scroll categories left"
                type="button"
              >
                <ChevronLeftIcon />
              </button>

              <div className="pf-track" ref={chemScroll.ref}>
                {chemicalCategories.map(chem => (
                  <button
                    key={chem.id}
                    className={`prods-chem-pill${String(chemicalCategory) === String(chem.id) ? ' active' : ''}`}
                    onClick={(e) => {
                      handleChemSelect(chem.id);
                      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                  >
                    <span className="prods-chem-pill-dot" />
                    {chem.name}
                  </button>
                ))}

                {chemicalCategory && (
                  <button className="prods-chem-clear" onClick={() => { setChemicalCategory(''); setPage(1); }}>
                    ✕ Clear
                  </button>
                )}
              </div>

              <button
                className={`pf-arrow right${chemScroll.canRight ? '' : ' disabled'}`}
                onClick={() => chemScroll.scrollByAmount(220)}
                aria-label="Scroll categories right"
                type="button"
              >
                <ChevronRightIcon />
              </button>

              <div className={`pf-fade left${chemScroll.canLeft ? ' visible' : ''}`} />
              <div className={`pf-fade right${chemScroll.canRight ? ' visible' : ''}`} />
            </div>
          </div>
        </div>
      )}

      {/* ── FILTER BAR ── */}
      <div className="prods-filterbar">
        <div className="pf-bar">
          {categories.length > 0 && (
            <>
              <span className="pf-label">Category</span>

              <div className="pf-scroll">
                <button
                  className={`pf-arrow left${filterScroll.canLeft ? '' : ' disabled'}`}
                  onClick={() => filterScroll.scrollByAmount(-220)}
                  aria-label="Scroll categories left"
                  type="button"
                >
                  <ChevronLeftIcon />
                </button>

                <div className="pf-track" ref={filterScroll.ref}>
                  <button
                    className={`prods-filter-btn${activeCategory === 'All' ? ' active' : ''}`}
                    onClick={(e) => {
                      setActiveCategory('All'); setPage(1);
                      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                  >
                    All
                    <span className="prods-filter-count">{totalCount}</span>
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      className={`prods-filter-btn${activeCategory === String(cat.id) ? ' active' : ''}`}
                      onClick={(e) => {
                        setActiveCategory(String(cat.id)); setPage(1);
                        e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                <button
                  className={`pf-arrow right${filterScroll.canRight ? '' : ' disabled'}`}
                  onClick={() => filterScroll.scrollByAmount(220)}
                  aria-label="Scroll categories right"
                  type="button"
                >
                  <ChevronRightIcon />
                </button>

                <div className={`pf-fade left${filterScroll.canLeft ? ' visible' : ''}`} />
                <div className={`pf-fade right${filterScroll.canRight ? ' visible' : ''}`} />
              </div>
            </>
          )}

          <div className="pf-controls">
            <div className="prods-sort-wrap">
              <span className="prods-sort-label">Sort</span>
              <select className="prods-sort-select" value={sort} onChange={e => setSort(e.target.value)}>
                <option value="order">Default Order</option>
                <option value="name">A – Z</option>
                <option value="name_desc">Z – A</option>
                <option value="newest">Newest</option>
              </select>
            </div>

            <div className="prods-rx-toggle">
              <span className="prods-rx-label">Rx Only</span>
              <Toggle on={rxOnly} onToggle={() => setRxOnly(v => !v)} />
            </div>

            <span className="prods-results-count">
              {loading ? '…' : `${displayed.length} result${displayed.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
      </div>

      {/* ── GRID SECTION ── */}
      <section className="prods-grid-section">

        {error && <div className="prods-error">{error}</div>}

        {loading ? (
          <div className="prods-grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <SkeletonCard key={i} index={i} />
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="prods-empty">
            <p className="prods-empty-title">Nothing found</p>
            <p className="prods-empty-sub">
              {search
                ? `No products match "${search}"`
                : rxOnly
                  ? 'No Rx-only products in this category'
                  : 'No products found for the current filters'}
            </p>
            <button className="prods-empty-btn" onClick={resetAll}>
              Reset Filters
            </button>
          </div>
        ) : (
          <>
            <div className="prods-grid">
              {displayed.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={i}
                  onClick={() => navigate(productPath(p))}
                />
              ))}
            </div>

            {/* Pagination */}
            {(meta.has_previous || meta.has_next) && (
              <div className="prods-pagination">
                <button
                  className="prods-page-btn"
                  disabled={!meta.has_previous}
                  onClick={() => setPage(v => Math.max(v - 1, 1))}
                >
                  ← Previous
                </button>
                <span className="prods-page-current">
                  {meta.page || page} / {meta.total_pages || 1}
                </span>
                <button
                  className="prods-page-btn"
                  disabled={!meta.has_next}
                  onClick={() => setPage(v => v + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── FOOTER CTA ── */}
      <div className="prods-footer-cta">
        <p className="prods-footer-cta-label">Connect With Us</p>
        <h2>Explore our full pharmaceutical range</h2>
        <a href="/contact" className="prods-footer-cta-btn">
          Contact Us <ArrowIcon />
        </a>
      </div>

    </div>
  );
}
